/* The Home page: where each report has got to, against where it should be by now.
 * "By now" depends on today's month: a quarterly report should cover the last finished quarter (October -> Q3) and a monthly
 * report the last finished month (October -> September). Works on the report list (no figures needed) and the approval rows. */
import { stageLabel, statusOf, AM, FINANCE } from './approvals.js';
import { managerOf } from './constants.js';
import { MONTH_NAMES, frequencyOf, periodOf } from './periods.js';

export { frequencyOf };

const monthly = (year, month) => ({ sel: { mode: 'monthly', year, month }, label: `${MONTH_NAMES[month]} ${year}`, index: year * 12 + month });
const quarterly = (year, quarter) => ({ sel: { mode: 'quarterly', year, quarter }, label: `Q${quarter + 1} ${year}`, index: year * 4 + quarter });

/** The last finished period for `frequency` in the month of `today` -> { sel, label, index }.
 * A quarterly report falls due only when a quarter has ended, so its period moves on in January, April, July and October
 * and stays put for the two months after; a monthly report moves on every month. */
export function expectedPeriod(frequency, today = new Date()) {
  const y = today.getFullYear(), m = today.getMonth();
  if (frequency === 'monthly') return m === 0 ? monthly(y - 1, 11) : monthly(y, m - 1);
  const q = Math.floor(m / 3) - 1;
  return q < 0 ? quarterly(y - 1, 3) : quarterly(y, q);
}

/** The period a report should have reached in `year`: the most recent reporting period for the current year, and the last
 * period of the year (December / Q4) for an earlier one. */
export function referencePeriod(frequency, today = new Date(), year = today.getFullYear()) {
  if (year >= today.getFullYear()) return expectedPeriod(frequency, today);
  return frequency === 'monthly' ? monthly(year, 11) : quarterly(year, 3);
}

/** The years offered on the Home page, newest first: every year with a report, and always the current year. */
export function homeYears(reports, today = new Date()) {
  return [...new Set([today.getFullYear(), ...reports.map((r) => r.report_year)])].sort((a, b) => b - a);
}

/** Does this report belong to the signed-in account manager? (first-name match, as for AM approval) */
export const ownedBy = (user, report) => !!user && String(user.account_manager || '').trim().toLowerCase() === managerOf(report).toLowerCase();

/** The last period a report is complete for, from its as-at date (a period counts once the as-at date reaches its last day). */
function reportedPeriod(frequency, asAt) {
  const [y, mo, d] = String(asAt).split('-').map(Number);
  const endsMonth = d >= new Date(y, mo, 0).getDate();
  const month = endsMonth ? mo - 1 : mo - 2;                       // 0-11 of the last complete month (may be -1)
  const year = month < 0 ? y - 1 : y, mi = (month + 12) % 12;
  if (frequency === 'monthly') return monthly(year, mi);
  const q = Math.floor((mi + 1) / 3) - 1;                          // a quarter is complete once its last month is
  return q < 0 ? quarterly(year - 1, 3) : quarterly(year, q);
}

/** One row per report id, most overdue first, for `year` (default: the current year).
 * -> { id, partner, manager, frequency, state: 'current'|'behind', behind, reportedTo, expected, stage, approval, finance, account_manager,
 *      report, dueReport, awaiting, awaitingLevel }
 * `expected` is the period due: the most recent reporting period this year, or the last period of an earlier year.
 * stage/approval are for the period the report has reached; finance / account_manager are the approvals (or null) for the period due.
 * `awaiting` is true when the period due has been reported but is not yet approved by both Finance and the Account Manager;
 * `awaitingLevel` says who is next ('finance' or 'account_manager'); `dueReport` is the report to open for that period. */
export function buildHome(reports, approvals, today = new Date(), year = today.getFullYear()) {
  const ids = [...new Set(reports.map((r) => r.settlement_report_id))];
  const newest = (list) => list.reduce((a, b) => (!a || b.as_at_date > a.as_at_date ? b : a), null);
  const rows = ids.map((id) => {
    const mine = reports.filter((r) => r.settlement_report_id === id);
    const frequency = frequencyOf(newest(mine));
    const expected = referencePeriod(frequency, today, year);
    const r = newest(mine.filter((x) => x.report_year <= expected.sel.year));    // nothing yet for a report that starts after this year
    if (!r) return null;
    const reachedAt = reportedPeriod(frequency, r.as_at_date);
    const behind = Math.max(0, expected.index - reachedAt.index);
    const st = statusOf(approvals, r, periodOf(reachedAt.sel));
    const due = newest(mine.filter((x) => x.report_year === expected.sel.year));
    const dueStatus = due && !behind ? statusOf(approvals, due, periodOf(expected.sel)) : { finance: null, account_manager: null, stage: 'none' };
    const awaiting = !!due && !behind && dueStatus.stage !== 'complete';
    return { id, partner: r.client_name || r.company_name || '', manager: r.account_manager || '', frequency,
      state: behind ? 'behind' : 'current', behind, reportedTo: reachedAt, expected, stage: st.stage, approval: stageLabel(st.stage),
      finance: dueStatus.finance, account_manager: dueStatus.account_manager, report: r, dueReport: behind ? null : due || null,
      awaiting, awaitingLevel: awaiting ? (dueStatus.finance ? AM : FINANCE) : null };
  }).filter(Boolean);
  return rows.sort((a, b) => b.behind - a.behind || a.id.localeCompare(b.id));
}
